/*
 * DownloadAppDialog
 * -----------------
 * Single source-of-truth for "Get the App" / "Get the driver app" CTAs on
 * the public marketing site. Any component can open it by dispatching:
 *
 *     import { openDownloadDialog } from "@/components/DownloadAppDialog";
 *     openDownloadDialog("driver");   // or "passenger" or "general"
 *
 * The steps must match the app's real sign-up flow: email and password,
 * a 6-digit email code, then choose Passenger or Driver.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import StoreBadges from "@/components/marketing/StoreBadges";

/* ─── Public API: open the dialog from anywhere ─────────────────────── */

export type DownloadDialogMode = "passenger" | "driver" | "general";

const EVENT_NAME = "hp:open-download-dialog";

/**
 * Opens the download dialog with copy for the given audience.
 * @param mode which audience the copy is written for
 */
export function openDownloadDialog(mode: DownloadDialogMode = "general") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, { detail: { mode } })
  );
}

/* ─── Mode-specific copy ────────────────────────────────────────────── */

const MODE_COPY: Record<
  DownloadDialogMode,
  { eyebrow: string; title: string; body: string; steps: string[] }
> = {
  passenger: {
    eyebrow: "For riders",
    title: "Download H-Plate to ride",
    body: "Live buses, official fares in EC$ and one-tap SOS. Free on iPhone and Android.",
    steps: [
      "Download H-Plate from the App Store or Google Play.",
      "Sign up with your email and enter the 6-digit code we send you.",
      "Choose Passenger, and you're ready to ride.",
    ],
  },
  driver: {
    eyebrow: "For H-bus drivers",
    title: "Use H-Plate on your bus",
    body: "You run your own bus. H-Plate shows you riders waiting on your route. Free to use, and H-Plate takes no cut of your fares.",
    steps: [
      "Download H-Plate from the App Store or Google Play.",
      "Sign up with your email, then choose Driver.",
      "Add your bus and upload your licence and a selfie. When they're approved you can go online, then add your registration and insurance.",
    ],
  },
  general: {
    eyebrow: "Get H-Plate",
    title: "Download the app",
    body: "H-Plate is free for riders and drivers, on iPhone and Android.",
    steps: [
      "Download H-Plate from the App Store or Google Play.",
      "Sign up with your email and enter the 6-digit code we send you.",
      "Choose Passenger or Driver, and you're set.",
    ],
  },
};

/* ─── Component ─────────────────────────────────────────────────────── */

/**
 * Download dialog for the public site. Mount once per page; open it with
 * `openDownloadDialog()`.
 */
export default function DownloadAppDialog() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<DownloadDialogMode>("general");

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ mode?: DownloadDialogMode }>).detail;
      setMode(detail?.mode ?? "general");
      setOpen(true);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const copy = MODE_COPY[mode];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-2xl">
        <div className="px-7 pt-8 pb-7">
          <img
            src="/logo-transparent.png"
            alt=""
            width={44}
            height={44}
            className="w-11 h-11 object-contain"
          />
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">
            {copy.eyebrow}
          </p>
          <DialogTitle className="mt-2 font-display text-[28px] leading-tight tracking-tight text-slate-900">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="mt-2 text-[15px] leading-relaxed text-slate-600">
            {copy.body}
          </DialogDescription>

          <ol className="mt-6 space-y-4">
            {copy.steps.map((step, idx) => (
              <li key={step} className="flex items-start gap-3.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
                  {idx + 1}
                </span>
                <span className="text-[15px] leading-snug text-slate-700">{step}</span>
              </li>
            ))}
          </ol>

          <StoreBadges className="mt-7" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
